
import { Helmet } from "react-helmet";
import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { LineChart, BarChart } from "@/components/ui/chart";
import { Button } from "@/components/ui/button";
import { FileEdit, Shuffle, BookOpen, ArrowRight, Download, Palette } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  
  // State for statistics
  const [stats, setStats] = useState({
    totalPosts: 0,
    totalPageViews: 0,
    activeRedirects: 0
  });

  // Load data on component mount
  useEffect(() => {
    if (!loading) {
      loadDashboardData();
    }
  }, [loading]);

  const loadDashboardData = () => {
    // Load blog posts from localStorage (our data store for this demo)
    const savedPosts = localStorage.getItem('edgefi_blog_posts');
    const blogPosts = savedPosts ? JSON.parse(savedPosts) : [];
    
    // Load redirects count if available
    const savedRedirects = localStorage.getItem('edgefi_redirects');
    const redirects = savedRedirects ? JSON.parse(savedRedirects) : [];
    
    // Update stats
    setStats({
      totalPosts: blogPosts.length,
      totalPageViews: Math.floor(Math.random() * 10000) + 5000, // Simulated value
      activeRedirects: redirects.length
    });
  };

  // Quick action cards with consistent styling
  const quickActions = [
    {
      title: "Manage Blog Posts",
      icon: BookOpen,
      path: "/admin/blog",
      description: "Create, edit, and publish blog content"
    },
    {
      title: "WordPress Import",
      icon: Download,
      path: "/admin/wordpress-import",
      description: "Import content from WordPress"
    },
    {
      title: "Brand Guidelines",
      icon: Palette,
      path: "/admin/brand-book",
      description: "Access brand assets and guidelines"
    }
  ];

  // Mock data for charts
  const visitorsData = {
    labels: ["Jan", "Feb", "Mar", "Apr", "May", "Jun"],
    datasets: [
      {
        label: "Website Visitors",
        data: [1420, 1620, 1820, 2200, 2500, 2900],
        borderColor: "#6501E5",
        backgroundColor: "rgba(101, 1, 229, 0.1)",
        tension: 0.3,
      },
    ],
  };

  const blogViewsData = {
    labels: ["Security", "Microsoft 365", "Managed IT", "Cybersecurity", "Compliance"],
    datasets: [
      {
        label: "Blog Views by Category",
        data: [5243, 4800, 3200, 2800, 2100],
        backgroundColor: "#6501E5",
      },
    ],
  };

  // Mock activity data
  const recentActivity = [
    {
      type: "blog",
      title: "New blog post published",
      date: "June 12, 2023 at 10:30 AM",
      color: "blue"
    },
    {
      type: "redirect",
      title: "Redirect rule added",
      date: "June 10, 2023 at 4:15 PM",
      color: "green"
    },
    {
      type: "content",
      title: "Content updated on Home page",
      date: "June 8, 2023 at 2:45 PM",
      color: "purple"
    }
  ];

  return (
    <div className="space-y-6">
      <Helmet>
        <title>Admin Dashboard | edgefi</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
        <p className="text-gray-500">Welcome to the edgefi admin dashboard</p>
      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Blog Posts</CardTitle>
            <CardDescription>All time published posts</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{stats.totalPosts}</div>
            <div className="mt-1 flex items-center justify-between">
              <p className="text-xs text-green-500">+12% from last month</p>
              <Link to="/admin/blog">
                <Button variant="ghost" size="sm" className="text-xs flex items-center gap-1 text-edgefi-purple">
                  <BookOpen size={12} />
                  Manage Posts
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Page Views</CardTitle>
            <CardDescription>Last 30 days</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{stats.totalPageViews.toLocaleString()}</div>
            <div className="mt-1 flex items-center justify-between">
              <p className="text-xs text-green-500">+5.4% from previous period</p>
              <Link to="/admin/content">
                <Button variant="ghost" size="sm" className="text-xs flex items-center gap-1 text-edgefi-purple">
                  <FileEdit size={12} />
                  Edit Content
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Active Redirects</CardTitle>
            <CardDescription>Current redirect rules</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{stats.activeRedirects}</div>
            <div className="mt-1 flex items-center justify-between">
              <p className="text-xs text-gray-500">Last updated 3 days ago</p>
              <Link to="/admin/redirects">
                <Button variant="ghost" size="sm" className="text-xs flex items-center gap-1 text-edgefi-purple">
                  <Shuffle size={12} />
                  Manage Redirects
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="mb-6">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {quickActions.map((action, index) => (
              <Link to={action.path} key={index} className="no-underline">
                <Card className="h-full hover:border-edgefi-purple/50 hover:shadow-md transition-all">
                  <CardContent className="p-4 flex flex-col gap-2">
                    <div className="flex items-center gap-3 mb-1">
                      <div className="p-2 rounded-lg bg-edgefi-purple/10">
                        <action.icon className="h-5 w-5 text-edgefi-purple" />
                      </div>
                      <h3 className="font-semibold">{action.title}</h3>
                    </div>
                    <p className="text-sm text-gray-500">{action.description}</p>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="traffic">
        <TabsList>
          <TabsTrigger value="traffic">Traffic</TabsTrigger>
          <TabsTrigger value="content">Content Performance</TabsTrigger>
        </TabsList>
        
        <TabsContent value="traffic" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Website Visitors</CardTitle>
              <CardDescription>Traffic over the last 6 months</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[300px]">
                <LineChart data={visitorsData} />
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="content" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Blog Views by Category</CardTitle>
              <CardDescription>Most popular content categories</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[300px]">
                <BarChart data={blogViewsData} />
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Recent Activity</CardTitle>
            <Button variant="ghost" size="sm" className="text-xs flex items-center gap-1">
              View All <ArrowRight size={12} />
            </Button>
          </CardHeader>
          <CardContent>
            <ul className="space-y-4">
              {recentActivity.map((activity, index) => (
                <li key={index} className="flex items-start gap-4">
                  <div className={`rounded-full bg-${activity.color}-100 p-2`}>
                    <div className={`h-2 w-2 rounded-full bg-${activity.color}-600`}></div>
                  </div>
                  <div>
                    <p className="text-sm font-medium">{activity.title}</p>
                    <p className="text-xs text-gray-500">{activity.date}</p>
                  </div>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Quick Stats</CardTitle>
            <Button variant="ghost" size="sm" className="text-xs flex items-center gap-1">
              Refresh <ArrowRight size={12} />
            </Button>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div className="rounded-lg border p-3">
                <p className="text-xs font-medium text-gray-500">Top Page</p>
                <p className="text-sm font-medium mt-1">Home Page</p>
                <p className="text-xs text-green-500 mt-1">4,230 views</p>
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-xs font-medium text-gray-500">Top Blog Post</p>
                <p className="text-sm font-medium mt-1">M365 Security Guide</p>
                <p className="text-xs text-green-500 mt-1">1,845 views</p>
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-xs font-medium text-gray-500">Avg. Time on Site</p>
                <p className="text-sm font-medium mt-1">3:24 minutes</p>
                <p className="text-xs text-green-500 mt-1">+18% increase</p>
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-xs font-medium text-gray-500">Bounce Rate</p>
                <p className="text-sm font-medium mt-1">32.8%</p>
                <p className="text-xs text-green-500 mt-1">-5.2% decrease</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
